import {
  Injectable,
  signal,
  PLATFORM_ID,
  inject,
  OnDestroy,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SystemResponse } from '../../../features/dashboard/models/SystemsModel';
import { SystemsService } from '../../../features/dashboard/services/systems-service';
import { LoaderService } from '../loader/loader-service';
import { KeyStorageUtil } from '../../../shared/utils/keystorage';
import { UtilCatService } from '../../../shared/utils/UtilCatService';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  throwError,
  BehaviorSubject,
  Subscription,
  filter,
} from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class SidebarService implements OnDestroy {
  private _isOpen = signal(true);
  private platformId = inject(PLATFORM_ID);
  private resizeListener?: () => void;
  private navigationSubscription?: Subscription;
  private _systemsUpdated = new BehaviorSubject<SystemResponse[]>([]);
  public systemsUpdated$: Observable<SystemResponse[]> =
    this._systemsUpdated.asObservable();

  readonly isOpen = this._isOpen.asReadonly();

  constructor(
    private systemsService: SystemsService,
    private loaderService: LoaderService,
    private router: Router
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.setupResizeListener();
      this.checkInitialScreenSize();
      this.setupNavigationListener();
    }
  }

  notifySystemsUpdated(systems: SystemResponse[]): void {
    this._systemsUpdated.next(systems);
  }

  private setupResizeListener() {
    this.resizeListener = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth < 640) {
        this.close();
      } else {
        this.open();
      }
    };

    window.addEventListener('resize', this.resizeListener);
  }

  private setupNavigationListener() {
    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (window.innerWidth < 640) {
          this.close();
        }
      });
  }

  private checkInitialScreenSize() {
    const screenWidth = window.innerWidth;
    if (screenWidth < 640) {
      this.close();
    }
  }

  toggle() {
    this._isOpen.set(!this._isOpen());
  }

  close() {
    this._isOpen.set(false);
  }

  open() {
    this._isOpen.set(true);
  }

  ngOnDestroy() {
    if (this.resizeListener && isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeListener);
    }
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  //systems data
  //user data
  getSystems(isAdmin: boolean): Observable<SystemResponse[]> {
    this.loaderService.showLoader();
    const cachedSystems = sessionStorage.getItem('sidebarSystems');
    if (cachedSystems) {
      this.loaderService.hideLoader();
      const systems = JSON.parse(cachedSystems);
      return of(systems);
    } else {
      return this.systemsService.fetchSystemsData().pipe(
        map((res: SystemResponse[]) => {
          KeyStorageUtil.setItem('systems', res);
          let processedSystems: SystemResponse[];
          if (isAdmin) {
            processedSystems = res;
          } else {
            processedSystems = UtilCatService.getUserUniqueSystems();
            processedSystems.forEach((system: SystemResponse) => {
              system.img =
                res.find((s) => s.nombre === system.nombre)?.img || '';
            });
          }
          sessionStorage.setItem(
            'sidebarSystems',
            JSON.stringify(processedSystems)
          );
          return processedSystems;
        }),
        finalize(() => {
          this.loaderService.hideLoader();
        }),
        catchError((err) => {
          console.error('Error fetching systems data:', err);
          this.loaderService.hideLoader();
          return throwError(() => err);
        })
      );
    }
  }
}
